import type { StrategyFormState, StrategyAction } from '../../types/strategy';
import { COLORS } from '../../constants/theme';
import { NumericInput } from './NumericInput';
import { acresForPct } from '../buildings/buildingsPercent';

interface EconomyTabProps {
  state: StrategyFormState;
  dispatch: React.Dispatch<StrategyAction>;
  totalLand: number;
  /** SF-172: `'touch'` renders 44px-min-height/16px-font `NumericInput`s (mobile); default
   *  preserves today's exact desktop styling. */
  inputSize?: 'default' | 'touch';
  /** SF-172: forwarded to every `NumericInput`'s own `resetKey` — clears any in-progress typed
   *  draft on an external bulk mutation (e.g. loading a template mid-edit). */
  resetKey?: number;
}

// Order matches the Buildings page left-to-right reading order (BUILDING_PAIRS).
const BUILDING_ROWS: { key: string; name: string; formula: string }[] = [
  { key: 'HOME', name: 'Homes', formula: 'Capacity' },
  { key: 'FARM', name: 'Farms', formula: 'Flat rate' },
  { key: 'MILL', name: 'Mills', formula: 'Parabolic %' },
  { key: 'BANK', name: 'Banks', formula: 'Flat + %' },
  { key: 'TRAINING_GROUND', name: 'Training Grounds', formula: 'Linear %' },
  { key: 'ARMOURY', name: 'Armouries', formula: 'Linear %' },
  { key: 'MILITARY_BARRACK', name: 'Barracks', formula: 'Pending' },
  { key: 'FORT', name: 'Forts', formula: 'Linear %' },
  { key: 'GUARD_STATION', name: 'Guard Stations', formula: 'Pending' },
  { key: 'HOSPITAL', name: 'Hospitals', formula: 'Pending' },
  { key: 'GUILD', name: 'Guilds', formula: 'Spell mechanics' },
  { key: 'TOWER', name: 'Towers', formula: 'Flat rate' },
  { key: 'THIEVES_DEN', name: "Thieves' Dens", formula: 'Pending' },
  { key: 'WATCH_TOWER', name: 'Watchtowers', formula: 'Pending' },
  { key: 'LIBRARY', name: 'Libraries', formula: 'Linear %' },
  { key: 'SCHOOL', name: 'Schools', formula: 'Parabolic %' },
  { key: 'STABLE', name: 'Stables', formula: 'Flat rate' },
  { key: 'DUNGEON', name: 'Dungeons', formula: 'Capacity' },
];

export function EconomyTab({ state, dispatch, totalLand, inputSize, resetKey }: EconomyTabProps) {
  const totalPct = Object.values(state.buildings).reduce((sum, v) => sum + (v || 0), 0);

  return (
    <div className="space-y-4">
      {/* Budget Bar */}
      <div>
        <div className="flex justify-between text-xs mb-1" style={{ color: COLORS.text.tertiary }}>
          <span>Building Allocation</span>
          <span className={totalPct > 100 ? 'text-yellow-400' : ''} style={totalPct > 100 ? undefined : { color: COLORS.text.secondary }}>
            {totalPct.toFixed(0)}% / 100%
          </span>
        </div>
        <div className="w-full rounded-full h-2 overflow-hidden" style={{ backgroundColor: COLORS.border }}>
          <div
            className={`h-full rounded-full transition-all ${totalPct > 100 ? 'bg-yellow-500' : 'bg-blue-500'}`}
            style={{ width: `${Math.min(totalPct, 100)}%` }}
          />
        </div>
        {totalPct > 100 && (
          <div className="text-xs text-yellow-400 mt-1">
            Over-allocated by {(totalPct - 100).toFixed(0)}% — some buildings will be reduced proportionally.
          </div>
        )}
      </div>

      {/* Buildings Grid */}
      <div className="space-y-1">
        <div className="grid grid-cols-[1fr_80px_60px] gap-2 text-xs px-2" style={{ color: COLORS.text.tertiary }}>
          <span>Building</span>
          <span className="text-right">Target %</span>
          {/* SF-190 Component 11 (Free Win): "Count" didn't say what was being counted — the
              column shows the acre-equivalent of Target %. */}
          <span className="text-right">Target acres</span>
        </div>
        {BUILDING_ROWS.map((b) => {
          const pct = state.buildings[b.key] || 0;
          const count = Math.floor(totalLand * pct / 100);
          return (
            <div
              key={b.key}
              className="grid grid-cols-[1fr_80px_60px] gap-2 items-center px-2 py-1 rounded"
            >
              <div>
                <span className="text-sm" style={{ color: COLORS.text.primary }}>{b.name}</span>
                {/* SF-190 Component 11 (Free Win): until the tooltip follow-up ships (blocked on
                    SF-188's Tooltip primitive), hide unexplained formula jargon on touch rather
                    than showing it with no way to ask "what does this mean." Desktop unaffected. */}
                {inputSize !== 'touch' && (
                  <span className="text-xs ml-2" style={{ color: COLORS.text.muted }}>{b.formula}</span>
                )}
              </div>
              <NumericInput
                value={pct}
                max={100}
                onChange={(v) => dispatch({ type: 'SET_BUILDING', building: b.key, value: v })}
                className="w-16 ml-auto"
                ariaLabel={`${b.name} target percent`}
                size={inputSize}
                resetKey={resetKey}
              />
              <div className="text-right text-sm font-mono" style={{ color: COLORS.text.secondary }}>
                {count}
              </div>
            </div>
          );
        })}
        {/* SF-190 Component 8: synthetic, display-only row — not editable, no target/drafted
            state. Gated on at least one positive target existing at all (mirrors
            MobileBuildingsShell.tsx's activeStrategy?.settings?.buildings gate, translated to this
            form-editing context where `state.buildings` IS the strategy under edit — a totally
            blank form's "100% Unallocated" isn't itself informative). */}
        {totalPct > 0 && (() => {
          const unallocatedPct = Math.max(0, 100 - totalPct);
          const unallocatedAcres = acresForPct(totalLand, unallocatedPct);
          return (
            <div
              className="grid grid-cols-[1fr_80px_60px] gap-2 items-center px-2 py-1 rounded"
              style={{ borderTop: `1px dashed ${COLORS.border}` }}
            >
              <div>
                <span className="text-sm italic" style={{ color: COLORS.text.tertiary }}>Unallocated</span>
                <div className="text-xs" style={{ color: COLORS.text.muted }}>
                  Not assigned to any building — deliberate reserve or simply unallocated
                </div>
              </div>
              <div className="text-right text-sm font-mono" style={{ color: COLORS.text.tertiary }}>
                {unallocatedPct.toFixed(0)}%
              </div>
              <div className="text-right text-sm font-mono" style={{ color: COLORS.text.tertiary }}>
                {unallocatedAcres}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
